/**
 * Inventory > per-item stock history (tracker item 30): every INVOICE and
 * MANUAL move of one item from GET /inventory/adjustments, newest first and
 * paged. Invoice rows link to the invoice; receipts and count adjustments
 * link to their journal entry when one was posted.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IInventoryApi } from '../api';
import * as types from '../api/types';
import { secondaryButtonClass } from '../components/formStyles';
import {
  INVENTORY_HISTORY_PAGE_SIZE,
  QUANTITY_ONLY_NOTE,
  formatQuantityChange,
  historyKind,
  historyReferenceLink,
  inventoryDate,
  inventoryErrorMessage,
} from './inventoryUi';
import { Toast } from '../components/Toast';

interface InventoryHistoryPanelProps {
  api: IInventoryApi;
  itemId: string;
  /** Name from the valuation list; the rows' itemName is used when the item is not listed */
  itemName?: string;
  /** Bumped after a receipt or adjustment so the open history re-reads */
  reloadKey: number;
  onClose: () => void;
}

export function InventoryHistoryPanel({ api, itemId, itemName, reloadKey, onClose }: InventoryHistoryPanelProps) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<types.InventoryAdjustment[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The page renders this with key={itemId}, so another item starts again at page 1.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.listInventoryAdjustments(page, INVENTORY_HISTORY_PAGE_SIZE, { itemId })
      .then((result) => { if (active) { setRows(result.data); setPagination(result.pagination); } })
      .catch((cause) => { if (active) { setRows([]); setPagination(null); setError(inventoryErrorMessage(cause, 'Could not load the stock history')); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, itemId, page, reloadKey]);

  const name = itemName || rows[0]?.itemName || 'Item';
  const pages = Math.max(1, pagination?.pages || 1);

  return (
    <aside aria-labelledby="history-title" className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow" data-testid="history-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="history-title" className="text-base font-semibold text-gray-900">Stock history: {name}</h2>
        <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} onClick={onClose}>Close history</button>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {loading ? <p className="mt-4 text-sm">Loading history…</p> : error ? null : rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-600">No stock moves for this item yet.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
            <caption className="sr-only">Stock moves for {name}</caption>
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Type', 'Change', 'Memo', 'Reference'].map((heading) => (
                  <th key={heading} scope="col" className={`px-4 py-3 font-medium text-gray-600 ${heading === 'Change' ? 'text-right' : 'text-left'}`}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => {
                const reference = historyReferenceLink(row);
                return (
                  <tr key={row.id}>
                    <td data-label="Date" data-primary className="px-4 py-3 whitespace-nowrap">{inventoryDate(row.adjustmentDate)}</td>
                    <td data-label="Type" className="px-4 py-3 whitespace-nowrap">{historyKind(row)}</td>
                    <td data-label="Change" className="px-4 py-3 whitespace-nowrap text-right tabular-nums">{formatQuantityChange(row.qtyChange)}</td>
                    <td data-label="Memo" className="px-4 py-3 break-words">{row.memo || <span className="text-gray-500">—</span>}</td>
                    <td data-label="Reference" className="px-4 py-3 md:whitespace-nowrap">
                      {reference ? (
                        <Link to={reference.to} className="text-blue-700 hover:underline">{reference.label}</Link>
                      ) : row.referenceType === 'MANUAL' ? (
                        <span className="text-gray-500">{QUANTITY_ONLY_NOTE}</span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!error && pages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
          <button type="button" className={secondaryButtonClass} disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={loading || page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </aside>
  );
}
